import { resolveSafeRedirect } from './safe-redirect'

/**
 * The page a sign-in returns to, held across a sign-in that leaves /login
 * before it finishes (owner, 2026-10-06: a session that ends returns the
 * person to their page after sign-in).
 *
 * /login?next=<page> honours `next` itself for a one-step sign-in. Two
 * sign-ins finish elsewhere and used to drop it: a 2FA challenge finishes on
 * /verify-2fa, Microsoft / Google on /auth/callback after a trip off the site.
 * The login page HOLDS the page when it hands off; the finishing page TAKES it.
 *
 * sessionStorage: this tab only, gone when the tab closes, and it survives the
 * provider round trip (same tab, same origin). Every hand-off writes it —
 * holding nothing clears it — so a page from an abandoned attempt never leaks
 * into a later one. Validated going in and coming out.
 */
export const RETURN_PATH_KEY = 'hf-auth:return-to'

/** Hold `next` for the page that finishes this sign-in; clears it when there is none. */
export function holdReturnPath(next: string | null | undefined): void {
  try {
    const safe = resolveSafeRedirect(next, '')
    if (safe) sessionStorage.setItem(RETURN_PATH_KEY, safe)
    else sessionStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    // Storage blocked: the sign-in still works and lands on the fallback.
  }
}

/** The held page, once (it is cleared), or `fallback` when none is held. */
export function takeReturnPath(fallback = '/dashboard'): string {
  let held: string | null = null
  try {
    held = sessionStorage.getItem(RETURN_PATH_KEY)
    sessionStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    // Storage blocked: nothing was held.
  }
  return resolveSafeRedirect(held, fallback)
}

/** Forget the held page (the person went back to the sign-in page). */
export function dropReturnPath(): void {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    // Storage blocked: nothing to forget.
  }
}
